import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {loadModules, setDefaultOptions} from 'esri-loader';
import {GoogleAnalyticsService} from '../googleAnalytics/google-analytics.service';
import esri = __esri;

/**
 * Controls/creates the esri map object
 * Holds all of the default and cross widget values associated with the map object
 * The view reference components are ephemeral, so all of that data is stored here
 */
@Injectable({
  providedIn: 'root'
})
export class MapService {
  private mapLoadedObservable = new BehaviorSubject<esri.Map>(null);
  private viewRefLoadedObservable = new BehaviorSubject(false);
  private mapScaleChangedObservable = new BehaviorSubject(null);
  private mapExtentChangedObservable = new BehaviorSubject(null);
  private basemapChangedObservable = new BehaviorSubject<Object>(false);

   /**
   * Keeps track of of the view references for when a user swaps between them
   *
   * @private
   */
  private mapViewAr = [];

  private zoom = 7.5;
  private center: Array<number> = [-120, 47.5];
  private mapRef: esri.Map;
  private viewRef;
  private searchWidgetRef;
  private scale;
  private rotation;
  private elevationLayer;
  private options: any = { version: '4.21', insertCssBefore: true, css: false };
  private mapType = 2;
  private extentTracker = [];
  private extentIndex = -1;
  private holdZoom = false;
  private identifyObservable = new BehaviorSubject(null);
  private identifyWatchObs;
  private mapScale: any;
  private mapExtent: any;
  

  constructor(private ga: GoogleAnalyticsService ) {}

  public getmapLoadedObservable() { return this.mapLoadedObservable; }
  public getZoom() { return this.zoom; }
  public setZoom(zoom) { this.zoom = zoom; }
  public getCenter() { return this.center; }
  public setCenter(center) { this.center = center; }
  public setScale(scale) { this.scale = scale; }
  public setRotation(rotation) { this.rotation = rotation; }
  public setSearchWidget(widget) { this.searchWidgetRef = widget; }
  public setSceneViewGroundLayer() {
    if (this.mapRef.ground.layers.length <= 0) {
      this.mapRef.ground.layers.add(this.elevationLayer);
    }
  }
  public setMapViewGroundLayer() {
    this.mapRef.ground.layers.removeAll();
     }
  public getMapObject(): esri.Map { return this.mapRef; }
  public getMapType(): number { return this.mapType; }
  public setMapType(type) { this.mapType = type; }
  public destroyViewRef() {
    this.viewRef.destroy();
    this.viewRef = null;
  }
  public getMapViewAr() { return this.mapViewAr; }

  public setViewRef(view, alreadySetFlag?) { 
    this.viewRef = view; 
    view.popup.autoOpenEnabled = false; 
    this.mapScale = this.viewRef.scale; 
    this.mapExtent = this.viewRef.extent; 
    if (!alreadySetFlag) {
      this.mapViewAr.push(view);
    }
  }
  public getViewRef() { return this.viewRef; }
  public getExtentAr() { return this.extentTracker; }
  public setExtentAr(extent: esri.Extent) { this.extentTracker.push(extent); }
  public getExtentIndex() { return this.extentIndex; }
  public setExtentIndex(index) { this.extentIndex = index; }
  public holdZoomTrue() { this.holdZoom = true; }
  public setViewRefObservable(value) { this.viewRefLoadedObservable.next(value); }
  public getViewRefObservable() { return this.viewRefLoadedObservable.asObservable(); }
  public setMapScaleObservable(value) { this.mapScaleChangedObservable.next(value); }
  public getMapScaleObservable() { return this.mapScaleChangedObservable.asObservable(); }
  public setMapExtentObservable(value) { this.mapExtentChangedObservable.next(value); }
  public getMapExtentObservable() { return this.mapExtentChangedObservable.asObservable(); }
  public setBasemapObservable(value) { this.basemapChangedObservable.next(value); }
  public getBasemapObservable() { return this.basemapChangedObservable.asObservable(); }
  

  /**
   * Async creates the map object and then returns an observable call when it's done
   * @param observer: Observer to call on to signify that the map is complete
   * @param params: Map creation parameters
   */
  public async setupMap(observer, params: {
    zoom?: number,
    center?: Array<number>,
    basemap?: string
  }) {
    try {
      let basemap = 'streets';
      if (params.zoom) {
        this.zoom = params.zoom;
      }
      if (params.center) {
        this.center = params.center;
      }
      if (params.basemap) {
        basemap = params.basemap;
      }
      const map = await this.initializeMap(basemap);
      await this.houseKeeping(map, observer);
    } catch (error) {
      this.ga.exception(error, true);
    }
  }

  /**
   * Creates the map
   */
  public async initializeMap(basemap) {
    try {
      const [ElevationLayer, Ground, Map] = await loadModules([
        'esri/layers/ElevationLayer',
        'esri/Ground',
        'esri/Map'
      ], this.options);
      setDefaultOptions(this.options);
      this.elevationLayer = new ElevationLayer({
        url: '//elevation3d.arcgis.com/arcgis/rest/services/WorldElevation3D/Terrain3D/ImageServer'
      });
      const mapProperties: esri.MapProperties = {
        basemap,
      };
      const map: esri.Map = new Map(mapProperties);
      map.ground.navigationConstraint = {
        type: 'none'
      };

      // Watch for basemap changes
      const this$=this;
      var handle = map.watch("basemap.title", function(newValue, oldValue, property, mapObject) {
          if (mapObject && mapObject["basemap"]) {
            this$.setBasemapObservable(mapObject["basemap"]);
          }                   
      });
      return map;
    } catch (error) {
     this.ga.exception(error, true);
    }
  }

  /**
   * Sends the observable call that notifies any listeners that the map object was created
   * @param map: The map object
   * @param observable: The observable to call out on
   */
  private houseKeeping(map, observable) {
    this.mapRef = map;
    observable.next(map);
    this.ga.timing();
    return {unsubscribe() {}};
  }

  /**
   * Sets up the Esri watch commands for the previous extent button after the view object is initialized
   */
  public async setupPreviousExtentButton() {
    try {
      const [watchUtils] = await loadModules([
        'esri/core/watchUtils'
      ]);

      watchUtils.whenTrue(this.getViewRef(), 'stationary', () => {
        // Check for mapScale and mapExtent changes
        if (this.getMapScale() !== this.mapScale) {
          // mapScale has changed
          this.mapScale = this.getMapScale();
          this.setMapScaleObservable(this.mapScale);
        } else {
          // Check for mapExent changed only if mapscale hasn't changed, avoids firing 2 events
          if (this.getMapExtent() !== this.mapExtent) {
              // mapExtent has changed
              this.mapExtent = this.viewRef.extent.clone();
              this.setMapExtentObservable(this.mapExtent);
          }
        }
        if (this.getViewRef().extent) {
          const view = this.getViewRef().extent;
          const extentAr = this.getExtentAr();
          const extentIndex = this.getExtentIndex();
          if (!this.holdZoom) {
            this.setExtentAr(view.extent);
          } else {
            // slices the extent tracker array if the back button is used
            if (extentAr.length > 1) {
              extentAr.splice(extentIndex, extentAr.length - extentIndex);
            }
            extentAr.push(view.extent);
            this.holdZoom = false;
          }
          this.setExtentIndex(extentAr.length - 1);
        }
      });
    } catch (error) {
      this.ga.exception(error, true);
    }
  }

  /**
   * Creates the data for an expanded search widget that searches internal layers
   * @param layer: Search config object
   */
  public async createSearchLayers(layer) {
    try {
      const [FeatureLayer] = await loadModules([
        'esri/layers/FeatureLayer'
      ]);
      if (!layer.config) {
        layer.config = {};
      }
      layer.config.layer = new FeatureLayer({
        url: layer.url
      });
      return layer.config;
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

   /**
    * Return current mapscale.
    */
  public getMapScale() {
    if (this.viewRef && this.viewRef.scale) {
      return this.viewRef.scale;
    } else {
      return null;
    }
  }

  public getMapExtent() {
    if (this.viewRef && this.viewRef.extent) {
      return  this.viewRef.extent;
    } else {
      return null;
    }
  }

  /**
   * Get the layer object on the map by id.
   * @param mapRef: map reference
   * @param serviceLayerID: serviceLayerID for layer
   */
  public getLayerFromMapView(mapRef: any, serviceLayerID: string) {
    let mapLayerObj: any;
    if (mapRef && mapRef.map && mapRef.map.layers) {
      const layerViews = mapRef.map.layers;
      if (layerViews !== undefined) {
          const layerItems = layerViews.items;
          for (const layerItem of layerItems) {
              if (layerItem.id === serviceLayerID) {
                  mapLayerObj = layerItem;
                  break;
              }
          }
        }
    }
    return mapLayerObj;
  }

  public enableIdentify() {
    if (this.getViewRef()) {
      this.identifyWatchObs = this.getViewRef().on('click', (e) => {
        this.identifyObservable.next(e);
      });
    }
  }
  public getIdentifyObservable() { return this.identifyObservable.asObservable(); }

  public disableIdentify() {
    if (this.identifyWatchObs) {
      this.identifyObservable.next(null);
      this.identifyWatchObs.remove();
      this.identifyWatchObs = null;
    }
  }
}
